import * as React from 'react'
import { useViewTransitionNavigate } from '../../../hooks/useViewTransitionNavigate.ts'
import { useSnapshot } from 'valtio'
import { dataState } from '../../../store/dataState.ts'
import { deviceFromPet } from '../../../helpers/dataHelpers.ts'
import { ContactForm } from '../ContactForm.tsx'
import { IContact, IPet } from '../../../interfaces/types.ts'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faUserPlus } from '@fortawesome/free-solid-svg-icons/faUserPlus'
import { faPhone } from '@fortawesome/free-solid-svg-icons/faPhone'
import { faEnvelope } from '@fortawesome/free-solid-svg-icons/faEnvelope'
import { faPen } from '@fortawesome/free-solid-svg-icons/faPen'
import { faTrashCan } from '@fortawesome/free-solid-svg-icons/faTrashCan'
import { PopupPortal } from '../../../components/common/Popup/PopupPortal.tsx'
import { RemoveContact } from '../../../components/common/RemoveContact/RemoveContact.tsx'
import { AlertPreferences } from '../../../components/common/AlertPreferences/AlertPreferences.tsx'

export const ContactListDesktop: React.FC = () => {
    const contacts = [...(useSnapshot(dataState) as typeof dataState).contacts].sort((a, b) => a.firstName > b.firstName ? 1 : -1)
    const pets = (useSnapshot(dataState) as typeof dataState).pets
    const { navigate } = useViewTransitionNavigate()
    const [editingContact, setEditingContact] = React.useState<IContact | null>(null)
    const [isNew, setIsNew] = React.useState(false)
    const [deletingContact, setDeletingContact] = React.useState<IContact | null>(null)
    const [alertContact, setAlertContact] = React.useState<IContact | null>(null)
    const [alertPet, setAlertPet] = React.useState<IPet | null>(null)

    const handleEdit = (contact: IContact) => {
        setEditingContact(contact)
        setIsNew(false)
    }

    const handleAddNew = () => {
        setEditingContact(null)
        setIsNew(true)
    }

    const handleSaved = () => {
        setEditingContact(null)
        setIsNew(false)
    }

    const getPetNames = (petList: any[]) => {
        if (!petList?.length) return []
        return petList.map(petId => {
            const pet = pets.find(p => p._id === petId)
            return pet ? { name: pet.petName, image: pet.imageURL, color: pet.iconColor } : null
        }).filter(Boolean)
    }

    // Detect if a pet image is the default/placeholder (too busy to render at pill size)
    const isDefaultImage = (url: string | undefined) => {
        if (!url) return true
        return url.includes('gibi') || url.includes('logo') || url.includes('default')
    }

    const emptyState = (
        <div className={'flex flex-col items-center gap-4 py-12 text-gray-400'}>
            <FontAwesomeIcon icon={faUserPlus} size={'3x'} />
            <div className={'text-lg'}>No alert contacts yet</div>
            <div className={'text-sm opacity-70'}>Add someone to also receive alerts for your pets</div>
        </div>
    )

    const contactCards = contacts.map((contact: IContact) => {
        const assignedPets = getPetNames(contact.petList)

        return (
            <div key={contact._id}
                 className={'bg-primary text-primary-content rounded-xl p-4 flex flex-col gap-3 overflow-hidden'}>
                {/* Name row + delete + edit */}
                <div className={'flex items-center justify-between gap-2'}>
                    <div className={'text-xl font-bold truncate'}>
                        {contact.firstName} {contact.lastName}
                    </div>
                    <div className={'flex items-center gap-2 shrink-0'}>
                        <button
                            className={'btn btn-ghost btn-sm btn-circle text-primary-content'}
                            onClick={() => setDeletingContact(contact)}
                        >
                            <FontAwesomeIcon icon={faTrashCan} />
                        </button>
                        <button
                            className={'btn btn-ghost btn-sm btn-circle text-primary-content'}
                            onClick={() => handleEdit(contact)}
                        >
                            <FontAwesomeIcon icon={faPen} />
                        </button>
                    </div>
                </div>

                {/* Contact info */}
                <div className={'flex flex-wrap gap-4 text-sm font-medium opacity-80'}>
                    {contact.mobileNo && (
                        <div className={'flex items-center gap-1.5'}>
                            <FontAwesomeIcon icon={faPhone} className={'opacity-60'} size={'sm'} />
                            <span>{contact.mobileNo}</span>
                        </div>
                    )}
                    {contact.email && (
                        <div className={'flex items-center gap-1.5'}>
                            <FontAwesomeIcon icon={faEnvelope} className={'opacity-60'} size={'sm'} />
                            <span>{contact.email}</span>
                        </div>
                    )}
                </div>

                {/* Assigned pets */}
                {assignedPets.length > 0 && (
                    <div className={'grid grid-cols-[auto_1fr] items-start gap-x-3 pt-1'}>
                        <span className={'text-xs font-medium uppercase tracking-wide opacity-60 pt-1'}>Alerts for:</span>
                        <div className={'flex flex-wrap gap-2'}>
                            {assignedPets.map((pet: any) => (
                                <button
                                    key={pet.name}
                                    className={'flex items-center gap-1.5 bg-base-100 text-primary rounded-full px-3 py-1 hover:ring-2 hover:ring-secondary transition-shadow cursor-pointer'}
                                    onClick={() => {
                                        const fullPet = pets.find(p => p.petName === pet.name)
                                        if (fullPet) {
                                            setAlertContact(contact)
                                            setAlertPet(fullPet)
                                        }
                                    }}
                                    title={`Edit ${contact.firstName}'s alerts for ${pet.name}`}
                                >
                                    {isDefaultImage(pet.image) ? (
                                        <div className={'w-5 h-5 rounded-full shrink-0'} style={{ backgroundColor: pet.color || '#ccc' }} />
                                    ) : (
                                        <img src={pet.image} className={'w-5 h-5 rounded-full object-cover shrink-0'} alt={pet.name} />
                                    )}
                                    <span className={'text-xs font-semibold truncate max-w-[6rem]'}>{pet.name}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* SMS opt-in status */}
                <div className={'text-xs font-medium opacity-50'}>
                    SMS alerts: {contact.smsOptIn === 'Opt-in' ? '✓ Opted in' : 'Opted out'}
                </div>
            </div>
        )
    })

    const showForm = editingContact || isNew

    return (
        <div className={'flex flex-col gap-6 w-full max-w-2xl mx-auto pb-6 px-4 max-h-[90vh] overflow-y-auto'}>
            {/* Header */}
            <div className={'sticky top-0 z-10 flex items-start justify-between bg-base-100 pt-6 pb-2'}>
                <div>
                    <h1 className={'text-2xl font-bold text-primary m-0'}>Alert Contacts</h1>
                    <p className={'text-sm text-gray-500 mt-1'}>People who also receive alerts for your pets</p>
                </div>
                <div className={'flex items-center gap-2'}>
                    <button className={'btn btn-secondary btn-sm gap-2'} onClick={handleAddNew}>
                        <FontAwesomeIcon icon={faUserPlus} />
                        Add Contact
                    </button>
                    <button className={'btn btn-ghost btn-sm text-primary'} onClick={() => navigate('/map')}>
                        Close
                    </button>
                </div>
            </div>

            {/* Contact cards */}
            {contacts.length ? (
                <div className={'flex flex-col gap-3'}>
                    {contactCards}
                </div>
            ) : emptyState}

            {/* Edit/Add form in popup */}
            <PopupPortal open={!!showForm}>
                <div className={'w-full flex justify-center max-h-[90vh] overflow-y-auto'}>
                    <ContactForm
                        key={isNew ? 'new' : editingContact?._id}
                        init={isNew ? undefined : (editingContact || undefined)}
                        isCreate={isNew}
                        onSaved={handleSaved}
                        title={'Contact'}
                    />
                </div>
            </PopupPortal>

            {/* Delete confirmation dialog */}
            {deletingContact && (
                <RemoveContact
                    contact={deletingContact}
                    open={!!deletingContact}
                    setOpen={(o) => { if (!o) setDeletingContact(null) }}
                />
            )}

            {/* Alert preferences for a contact's pet */}
            {alertContact && alertPet && (
                <AlertPreferences
                    open={!!alertContact && !!alertPet}
                    setOpen={(o) => { if (!o) { setAlertContact(null); setAlertPet(null) } }}
                    deviceId={deviceFromPet(alertPet)?._id || ''}
                    petName={alertPet.petName}
                    contactId={alertContact._id}
                    contactName={`${alertContact.firstName} ${alertContact.lastName}`}
                />
            )}
        </div>
    )
}
